import { baseApi } from '@/lib/utils'

export interface Employee {
  user_id: number
  user_name: string
  user_email: string
  hourly_cost: string
  date_joined: string
  clickup_id?: number
  end_date: string
  start_date: string
  archived: boolean
  team_id: number | null
  team: string | null
  skills: string[] | null
  bot?: boolean
  bot_alerted?: boolean
  projects?: {
    list_id: number
    project_name: string
  }[]
  profile_picture?: string | null

  // Employee Master Details fields (synced from ClickUp — see the per-row
  // "Sync" action button). Read-only in the dashboard; ClickUp stays the
  // source of truth for these.
  employee_code?: string | null
  job_title?: string | null
  department?: string | null
  wv_unit?: string | null
  employment_type?: string | null
  gender?: string | null
  marital_status?: string | null
  phone?: string | null
  official_email?: string | null
  personal_email?: string | null
  joining_date?: string | null
  confirmation_date?: string | null
  probation_end_date?: string | null
  date_of_birth?: string | null
  notice_period_days?: number | null
  blood_group?: string | null
  emergency_contact_name?: string | null
  emergency_contact_number?: string | null
  current_location?: string | null
  reporting_manager_name?: string | null
  emp_status?: string | null
  master_synced_at?: string | null
  // true once master_synced_at is set — drives whether the row's Sync
  // action button shows at all.
  master_synced?: boolean
}

export interface EmployeeParams {
  page: number
  limit: number
  search?: string
  datatype?: string
  salarySetup?: number | boolean | undefined
  alertbot?: number | undefined
  team_id?: number | undefined
  space_name?: string
  client?: string
  account_manager?: string
  delivery_head?: string
  project_tl?: string
  status?: string
  startDate?: string
  endDate?: string
  activity?: string
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
  project_status?: string[]
  folder_name?: string[]
  department?: string[]
  team_member?: number[]
}

export interface EmployeesResponse {
  data: Employee[]
  pagination: {
    totalRecords: number
    totalPages: number
    currentPage: number
    limit: number
  }
}
export interface PendingCountsData {
  projectsPendingCount: number
  pendingEmployeesCount: number
  sync: {
    sync_started_at: string | null
    last_synced_at: string | null
  }
}

export interface PendingCountsResponse {
  success: boolean
  message: string
  data: PendingCountsData
}

export interface TimesheetEntry {
  id: string
  user?: {
    username: string
  }
  task?: {
    name: string
  }
  duration: string | number
  billable: boolean
  start: string | number
  end: string | number
  tags?: Array<{ name: string }>
}

export interface TimesheetResponse {
  data: TimesheetEntry[]
}

export interface GetTimesheetRequest {
  data: {
    start: number
    end: number
    userId: string | number
  }
}

export interface EmployeeHistoryItem {
  id: number
  hourly_cost: string | number
  start_date: string | null
  end_date: string | null
  created_at: string | null
}

export interface EmployeeHistoryResponse {
  message: string
  data: EmployeeHistoryItem[]
}

export interface EmployeeProjectDetails {
  employee: {
    user_id: number
    user_name: string
  }
  summary: {
    total_projects: number
    total_hours: number
    total_billable_hours: number
    total_non_billable_hours: number
    billable_cost: number | string
    non_billable_cost: number | string
  }
  project_breakdown: {
    project_id: number
    project_name: string
    list_id: string
    estimation_hours: number
    total_hours: number
    total_billable_hours: number
    total_non_billable_hours: number
    billable_cost: number | string
    non_billable_cost: number | string
  }[]
}

export interface EmployeeProjectTagBreakdown {
  employee: {
    user_id: number
    user_name: string
    profile_picture: string | null
  }
  project: {
    project_id: number
    project_name: string
    list_id: string
  }
  total_hours: number
  tags: {
    tag: string
    total_hours: number
    billable_hours: number
    non_billable_hours: number
    pct_of_employee_total: number
    total_cost?: number
    user_details: {
      user_id: number
      user_name: string
      profile_picture: string | null
      total_hours: number
      billable_hours: number
      non_billable_hours: number
      total_cost?: number
    }
  }[]
}

export interface NewSalaryPayload {
  id: number
  hourly_cost: number | string
  salary_start_date: string
  salary_end_date?: string | null
}

export interface TeamOption {
  id: number
  name: string
}

export interface TeamsResponse {
  status: string
  data: TeamOption[]
}

export const employeeApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getEmployees: builder.query<EmployeesResponse, EmployeeParams>({
      query: ({
        page = 1,
        limit = 10,
        search = '',
        salarySetup = true,
        alertbot,
        team_id,
        sort_by,
        order,
      }) => ({
        url: '/employees',
        params: {
          page,
          limit,
          search,
          salarySetup,
          alertbot,
          ...(team_id !== undefined && { team_id }),
          sort_by,
          order,
        },
      }),
      providesTags: ['Employees'],
    }),

    updateEmployee: builder.mutation<
      Employee,
      { id: string | number; data: Partial<Employee> }
    >({
      query: ({ id, data }) => ({
        url: `/employees/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Employees', { type: 'Skills', id: 'LIST' }],
    }),

    // Per-row "Sync" action button on the Employees page — pulls this one
    // employee's record from the ClickUp Employee Master Details list.
    syncEmployeeMaster: builder.mutation<
      { success: boolean; message: string; data: Partial<Employee> },
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `/employees/${id}/sync-master`,
        method: 'POST',
      }),
      invalidatesTags: ['Employees'],
    }),

    getTimesheet: builder.mutation<TimesheetResponse, GetTimesheetRequest>({
      query: ({ data }) => ({
        url: '/employees/timesheet',
        method: 'POST',
        body: data,
      }),
    }),
    gethistory: builder.query<EmployeeHistoryResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/employees/history/${id}`,
      }),
      providesTags: ['Employees'],
    }),
    newSalary: builder.mutation<
      { success: boolean; message: string },
      NewSalaryPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/employees/new-salary/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Employees'],
    }),
    getProjectandEmployeeCout: builder.query<PendingCountsResponse, void>({
      query: () => ({
        url: '/employees/count',
      }),
      providesTags: ['Employees', 'Projects'],
    }),
    getTeams: builder.query<
      TeamsResponse,
      { pending?: boolean | string } | void
    >({
      query: () => ({
        url: '/teams',
      }),
      transformResponse: (response: {
        success: boolean
        data: Array<{ id: number; name: string }>
      }): TeamsResponse => ({
        status: response.success ? 'ok' : 'error',
        data: (response.data || []).map(t => ({ id: t.id, name: t.name })),
      }),
      providesTags: [{ type: 'Teams', id: 'LIST' }],
    }),
    getProjectTypes: builder.query<{ success: boolean; data: string[] }, void>({
      query: () => ({
        url: '/employees/project-types',
      }),
    }),
    updateSalary: builder.mutation<
      void,
      {
        employeeId: number
        salaryId: number
        hourly_cost?: number
        start_date?: string
        end_date?: string | null
      }
    >({
      query: ({ employeeId, salaryId, ...body }) => ({
        url: `/employees/${employeeId}/salary-history/${salaryId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Employees'],
    }),

    deleteHistory: builder.mutation<
      void,
      { employeeId: number; salaryId: number }
    >({
      query: ({ employeeId, salaryId }) => ({
        url: `/employees/${employeeId}/salary/${salaryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Employees'],
    }),
    getEmployeeProjectDetails: builder.query<
      { success: boolean; data: EmployeeProjectDetails },
      {
        employeeId: number
        search?: string
        startDate?: string | undefined
        endDate?: string | undefined
      }
    >({
      query: ({ employeeId, search, startDate, endDate }) => ({
        url: `/employees/employee-details/${employeeId}`,
        params: { search, startDate, endDate },
      }),
    }),
    getEmployeeProjectTagBreakdown: builder.query<
      { success: boolean; data: EmployeeProjectTagBreakdown },
      { employeeId: number; projectId: number }
    >({
      query: ({ employeeId, projectId }) => ({
        url: `/employees/employee-details/${employeeId}/project/${projectId}`,
      }),
    }),
  }),
})

export const {
  useGetEmployeesQuery,
  useUpdateEmployeeMutation,
  useSyncEmployeeMasterMutation,
  useGetTimesheetMutation,
  useGethistoryQuery,
  useNewSalaryMutation,
  useGetProjectandEmployeeCoutQuery,
  useGetTeamsQuery,
  useUpdateSalaryMutation,
  useDeleteHistoryMutation,
  useGetProjectTypesQuery,
  useGetEmployeeProjectDetailsQuery,
  useGetEmployeeProjectTagBreakdownQuery,
} = employeeApi
